'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// WhatsApp SVG icon
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

interface Props {
  profileUrl: string;
  profileName: string;
  /** Permite a un layout "look fijo" (ej. Halloween) reemplazar el botón disparador por el
   * suyo propio, conservando el modal de envío tal cual. Si no se pasa, se usa el <Button>
   * por defecto. */
  renderTrigger?: (onClick: () => void) => React.ReactNode;
}

export default function WhatsAppShareButton({ profileUrl, profileName, renderTrigger }: Props) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');

  const cleanPhone = phone.replace(/\D/g, '');
  const isValid = cleanPhone.length >= 8;

  const buildUrl = () => {
    const text = encodeURIComponent(
      `¡Hola! 👋 Te comparto la tarjeta digital de ${profileName}, encuéntralo todo aquí:\n👉 ${profileUrl}\n\n💾 Ábrelo desde tu celular — puedes guardarlo en tu pantalla de inicio y funciona aunque no tengas señal.`
    );
    return `https://api.whatsapp.com/send/?phone=${cleanPhone}&text=${text}&type=phone_number&app_absent=0`;
  };

  const handleSend = () => {
    if (!isValid) {
      setError('Ingresa un número válido (mínimo 8 dígitos).');
      return;
    }
    window.open(buildUrl(), '_blank', 'noopener,noreferrer');
    setOpen(false);
    setPhone('');
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setPhone('');
    setError('');
  };

  return (
    <>
      {renderTrigger ? renderTrigger(() => setOpen(true)) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="text-[#25D366] border-[#25D366]/40 hover:bg-[#25D366]/10 hover:border-[#25D366]"
        >
          <WhatsAppIcon className="h-4 w-4 mr-1.5" />
          Compartir
        </Button>
      )}

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={handleClose}
        >
          <div
            className="bg-card rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
              <h2 className="font-semibold text-foreground text-base">Compartir por WhatsApp</h2>
            </div>

            <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground border border-border/50">
              <p className="font-medium text-foreground mb-0.5">Mensaje que se enviará:</p>
              <p>¡Hola! 👋 Te comparto la tarjeta digital de <span className="font-medium">{profileName}</span>, encuéntralo todo aquí: 👉 {profileUrl}</p>
              <p className="mt-1">💾 Ábrelo desde tu celular — puedes guardarlo en tu pantalla de inicio y funciona aunque no tengas señal.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="wa-phone">Teléfono destino (con código de país)</Label>
              <Input
                id="wa-phone"
                type="tel"
                placeholder="55xxxxxxxx"
                value={phone}
                onChange={e => { setPhone(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                autoFocus
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
              <p className="text-xs text-muted-foreground">Esto abrirá una nueva pestaña con el chat listo para enviar.</p>
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                onClick={handleSend}
                disabled={!isValid}
                className="flex-1 bg-[#25D366] hover:bg-[#1ebe57] text-white"
              >
                <WhatsAppIcon className="h-4 w-4 mr-1.5" />
                Abrir WhatsApp
              </Button>
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
